import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Divider, Form, Grid, GridColumn, Icon, Modal } from "semantic-ui-react";

import { CUSTOM_CONFIRM_TYPES, Can, CustomConfirm } from "components";
import { createFormHelper } from "lib/Form";
import { ACL_ACTIVITIES, ACL_RESOURCES, ACSES_COMMUNICATION_EVENT_TYPES, COMMUNICATION_EVENTS } from "models";
import { DynamicTableContent } from "modules/CommunicationTemplates/components";
import { useCommunicationSettingsQuery, useDeleteMediumMutation } from "modules/CommunicationTemplates/hooks";

import type { SMSMediumForm } from "modules/CommunicationTemplates/components";
import type { GroupedCommunicationMedium } from "modules/CommunicationTemplates/queryKeys";

type SmsEditorModalFormProps = {
  hasErrors: boolean;
  medium: GroupedCommunicationMedium;
  isUpdateMediumPending: boolean;
  onClose: () => void;
  onSubmit: (fields: SMSMediumForm) => void;
};

const FormHelper = createFormHelper<SMSMediumForm>();

export const SmsEditorModalForm = ({ hasErrors, onClose, onSubmit, medium, isUpdateMediumPending }: SmsEditorModalFormProps) => {
  const { t } = useTranslation();

  const [isDeleteTemplateModalOpen, setIsDeleteTemplateModalOpen] = useState(false);

  const { data: communicationSettings } = useCommunicationSettingsQuery();
  const { mutateAsync: deleteMedium, isPending: isDeletePending } = useDeleteMediumMutation();
  const { watch, handleSubmit, reset } = FormHelper.useFormContext();
  const [message, carBrand, carModel] = watch(["message", "car_brand", "car_model"]);

  const handleDeleteMedium = async () => {
    await deleteMedium(medium.communication_medium_id);
    onClose();
  };

  return (
    <>
      <Modal.Header className="padding-container">
        <div className="header-container">
          <span className="header-title"> {t("v8_sms_editor", "SMS Editor")} </span>

          <Form.Button type="button" className="orange" onClick={() => reset()}>
            {t("v8_revert_to_default", "Revert To Default")}
          </Form.Button>
        </div>
      </Modal.Header>
      <Divider fitted />

      <Modal.Content className="padding-container">
        <Grid columns="equal">
          <GridColumn>
            <DynamicTableContent
              isEmailTemplate={false}
              displayReceptionistInfo={false}
              isConfirmationEmailMedium={false}
              showKeylockerPlaceholders={Boolean(COMMUNICATION_EVENTS.OnlineCheckInAnswered === medium.event_type)}
              showAcsesPlaceholders={Boolean(ACSES_COMMUNICATION_EVENT_TYPES.DropBackPinSent === medium.acses_communication_event_type)}
              showParkingGateCodePlaceholder={Boolean(medium.event_type === COMMUNICATION_EVENTS.OnlineCheckInAnswered && communicationSettings?.send_parking_gate_code)}
            />
          </GridColumn>
        </Grid>
        {carBrand && (
          <Grid columns="equal">
            <GridColumn>
              <Form.Field required error={!carBrand && hasErrors}>
                <label>{t("v8_brand", "Brand")} </label>
                <FormHelper.Field.Input name="car_brand" inputProps={{ placeholder: t("v8_brand", "Brand"), fluid: true }} />
              </Form.Field>
            </GridColumn>
          </Grid>
        )}
        {carModel && (
          <Grid columns="equal">
            <GridColumn>
              <Form.Field required error={!carModel && hasErrors}>
                <label>{t("v8_model", "Model")} </label>
                <FormHelper.Field.Input name="car_model" inputProps={{ placeholder: t("v8_model", "Model"), fluid: true }} />
              </Form.Field>
            </GridColumn>
          </Grid>
        )}
        <Grid columns="equal">
          <GridColumn>
            <Form.Field required error={!message && hasErrors}>
              <label>
                {t("v8_sms_text", "SMS text")} {`(${message.length || 0}/1600)`}
              </label>
              <FormHelper.Field.TextArea name="message" textAreaProps={{ placeholder: t("v8_sms_text", "SMS text"), maxLength: 1600, style: { minHeight: 116 } }} />
            </Form.Field>
          </GridColumn>
        </Grid>
      </Modal.Content>
      <Divider fitted />

      <Modal.Actions className="padding-container">
        <div className="actions-buttons-main-container">
          {medium.car_brand && (
            <Can I={ACL_ACTIVITIES.Delete} the={ACL_RESOURCES.Mediums}>
              <div className="action-buttons-container">
                <Form.Button color="red" type="button" onClick={() => setIsDeleteTemplateModalOpen(true)} disabled={isDeletePending}>
                  {t("v8_delete", "Delete")}
                  <Icon className="trash" />
                </Form.Button>
              </div>
            </Can>
          )}
          <div className="save-cancel-buttons-container">
            <Form.Button className="light" onClick={onClose}>
              {t("v8_cancel", "Cancel")}
              <Icon className="xmark" />
            </Form.Button>
            <Can I={ACL_ACTIVITIES.Update} the={ACL_RESOURCES.Mediums}>
              <Form.Button type="submit" color="green" disabled={isUpdateMediumPending} loading={isUpdateMediumPending} onClick={handleSubmit(d => onSubmit(d))}>
                {t("v8_save", "Save")}
                <Icon className="check" />
              </Form.Button>
            </Can>
          </div>
        </div>
      </Modal.Actions>

      <CustomConfirm
        isLoading={isDeletePending}
        type={CUSTOM_CONFIRM_TYPES.Danger}
        isOpen={isDeleteTemplateModalOpen}
        handleConfirm={handleDeleteMedium}
        handleCancel={() => setIsDeleteTemplateModalOpen(false)}
        confirmMsg={t("v8_delete_confirm_message", "Are you sure that you want to delete this? You can't undo this action.")}
      />
    </>
  );
};
